import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuthStore } from "@/store/auth.store";
import type { ApiMessage, SendMessagePayload, CreateConversationPayload } from "@/types";
import {
  getConversations,
  createConversation,
  getMessages,
  sendMessage,
  getUnreadCount,
  markAsRead,
  getAllUsers,
  getContacts,
  updateMessage,
  deleteMessage,
} from "@/services/messaging.service";
import { getClassroomsList } from "@/services/classes.service";

export const messagingKeys = {
  conversations: ["conversations"] as const,
  messages: (id: number) => ["messages", id] as const,
  users: ["users"] as const,
  contacts: ["contacts"] as const,
  unreadCount: ["unread-count"] as const,
};

export function useConversations() {
  const token = useAuthStore((s) => s.token);
  const userId = useAuthStore((s) => s.user?.id);
  const emailVerified = useAuthStore((s) => !!s.user?.email_verified_at);
  return useQuery({
    queryKey: [...messagingKeys.conversations, userId],
    // `signal` transmis jusqu'à `fetchWithRetry` : une requête dont plus personne n'attend le
    // résultat (écran quitté, requête remplacée) cesse aussitôt de retenter, au lieu de poursuivre
    // son backoff pendant ~44 s et de remonter son échec depuis une tout autre page.
    queryFn: ({ signal }) => getConversations(signal),
    enabled: !!token && emailVerified,
    staleTime: 60 * 1000,
  });
}

export function useMessages(conversationId: number | null) {
  return useQuery({
    queryKey: messagingKeys.messages(conversationId!),
    queryFn: () => getMessages(conversationId!),
    enabled: conversationId != null,
    // Echo tient déjà le cache à jour (push + optimistic updates) : un refetch au focus
    // n'apporte rien et ne fait que dupliquer le fetch initial en réseau.
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

export function useUnreadCount() {
  const token = useAuthStore((s) => s.token);
  const userId = useAuthStore((s) => s.user?.id);
  const emailVerified = useAuthStore((s) => !!s.user?.email_verified_at);
  return useQuery({
    queryKey: [...messagingKeys.unreadCount, userId],
    queryFn: getUnreadCount,
    enabled: !!token && emailVerified,
    staleTime: Infinity,
  });
}

export function useMarkAsRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (conversationId: number) => markAsRead(conversationId),
    // `onSettled`, et non `onSuccess` : sur un échec, rien n'a changé côté serveur, donc rien à
    // rafraîchir — mais invalider quand même déclenchait deux requêtes de plus dans la foulée
    // immédiate d'un `PATCH` qui venait d'échouer. Sur un 429 (limite de débit), ces deux requêtes
    // tombaient dans la même fenêtre et échouaient à leur tour : un seul mark-as-read raté
    // remontait comme trois erreurs Sentry au lieu d'une (SUCCESCHOOL-FRONTEND-V2-5Q).
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: messagingKeys.unreadCount });
      queryClient.invalidateQueries({ queryKey: messagingKeys.conversations });
    },
  });
}

export function useUsers() {
  const token = useAuthStore((s) => s.token);
  const emailVerified = useAuthStore((s) => !!s.user?.email_verified_at);
  return useQuery({
    queryKey: messagingKeys.users,
    queryFn: getAllUsers,
    staleTime: 5 * 60 * 1000,
    enabled: !!token && emailVerified,
  });
}

export function useContacts() {
  const token = useAuthStore((s) => s.token);
  const emailVerified = useAuthStore((s) => !!s.user?.email_verified_at);
  return useQuery({
    queryKey: messagingKeys.contacts,
    queryFn: getContacts,
    staleTime: 5 * 60 * 1000,
    enabled: !!token && emailVerified,
  });
}

export function useClassroomsList(enabled = true) {
  const token = useAuthStore((s) => s.token);
  const emailVerified = useAuthStore((s) => !!s.user?.email_verified_at);
  return useQuery({
    queryKey: ["classrooms-list"],
    queryFn: ({ signal }) => getClassroomsList(signal),
    enabled: !!token && emailVerified && enabled,
  });
}

export function useCreateConversation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateConversationPayload) => createConversation(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: messagingKeys.conversations });
    },
  });
}

export function useSendMessage(conversationId: number) {
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);

  return useMutation({
    mutationFn: (payload: SendMessagePayload) => sendMessage(conversationId, payload),
    onMutate: async (payload) => {
      await queryClient.cancelQueries({ queryKey: messagingKeys.messages(conversationId) });

      const previousMessages = queryClient.getQueryData<ApiMessage[]>(
        messagingKeys.messages(conversationId),
      );

      const tempId = -(Date.now() + Math.floor(Math.random() * 1000));
      const localId = String(tempId);

      const optimisticMessage: ApiMessage = {
        id: tempId,
        _localId: localId,
        conversation_id: conversationId,
        user_id: user?.id ?? 0,
        body: payload.body,
        file_path: payload.attachment ? URL.createObjectURL(payload.attachment) : null,
        file_url: payload.attachment ? URL.createObjectURL(payload.attachment) : null,
        file_type: payload.attachment
          ? payload.attachment.type.startsWith("image/")
            ? "image"
            : "pdf"
          : null,
        is_edited: null,
        edited_at: null,
        is_pinned: null,
        pinned_at: null,
        user: {
          id: user?.id ?? 0,
          name: user ? `${user.first_name} ${user.last_name}` : "",
          email: user?.email ?? "",
        },
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };

      queryClient.setQueryData<ApiMessage[]>(messagingKeys.messages(conversationId), (old = []) => [
        ...old,
        optimisticMessage,
      ]);

      return { previousMessages, tempId, localId };
    },
    onSuccess: (realMessage, _payload, context) => {
      queryClient.setQueryData<ApiMessage[]>(messagingKeys.messages(conversationId), (old = []) =>
        old.map((m) =>
          m._localId === context?.localId ? { ...realMessage, _localId: context.localId } : m,
        ),
      );
      queryClient.invalidateQueries({ queryKey: messagingKeys.conversations });
    },
    onError: (_err, _payload, context) => {
      if (context?.previousMessages) {
        queryClient.setQueryData(messagingKeys.messages(conversationId), context.previousMessages);
      }
    },
  });
}
export function useUpdateMessage(conversationId: number) {
  const queryClient = useQueryClient();
  const userId = useAuthStore((s) => s.user?.id);

  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: string }) => {
      if (!userId) return Promise.reject(new Error("Non authentifié"));
      return updateMessage(id, body);
    },
    onMutate: async ({ id, body }) => {
      await queryClient.cancelQueries({ queryKey: messagingKeys.messages(conversationId) });

      const previousMessages = queryClient.getQueryData<ApiMessage[]>(
        messagingKeys.messages(conversationId),
      );

      queryClient.setQueryData<ApiMessage[]>(messagingKeys.messages(conversationId), (old = []) =>
        old.map((m) =>
          m.id === id ? { ...m, body, is_edited: true, edited_at: new Date().toISOString() } : m,
        ),
      );

      return { previousMessages };
    },
    onSuccess: () => {
      // Ne PAS invalider messages — le Reverb .message.updated s'en charge.
      // Invalider conversations pour mettre à jour la sidebar.
      queryClient.invalidateQueries({ queryKey: messagingKeys.conversations });
    },
    onError: (_err, _vars, context) => {
      if (context?.previousMessages) {
        queryClient.setQueryData(messagingKeys.messages(conversationId), context.previousMessages);
      }
    },
  });
}
export function useDeleteMessage(conversationId: number) {
  const queryClient = useQueryClient();
  const userId = useAuthStore((s) => s.user?.id);

  return useMutation({
    mutationFn: (id: number) => {
      if (!userId) return Promise.reject(new Error("Non authentifié"));
      return deleteMessage(id);
    },
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: messagingKeys.messages(conversationId) });

      const previousMessages = queryClient.getQueryData<ApiMessage[]>(
        messagingKeys.messages(conversationId),
      );

      queryClient.setQueryData<ApiMessage[]>(messagingKeys.messages(conversationId), (old = []) =>
        old.filter((m) => m.id !== id),
      );

      return { previousMessages };
    },
    onSuccess: () => {
      // Ne PAS invalider messages — le Reverb .message.deleted s'en charge.
      // Invalider conversations pour mettre à jour la sidebar.
      queryClient.invalidateQueries({ queryKey: messagingKeys.conversations });
    },
    onError: (_err, _id, context) => {
      if (context?.previousMessages) {
        queryClient.setQueryData(messagingKeys.messages(conversationId), context.previousMessages);
      }
    },
  });
}
